import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { pickPaymentTerminal, placeBlocksCash, resolvePaymentTerminals, isPaymentTerminalManual } from '@app/components/model/ticket/ui-paytef/ui-paytef-utils';
import { viewService } from '@app/modules/view';

import { Place } from '@app/modules/model/place';
import { Ticket } from '@app/modules/model/ticket';

import { AppConstants } from '@app/app.constants';
import { languageSupport, languageService } from '@app/modules/common/language'
import { dataService } from '@app/modules/data';

import { UiCashPayModal } from '@app/components/model/ticket/ui-cash/ui-cash';
import { UiCashGuardCashPayModal } from '@app/components/model/ticket/ui-cashguard-cash/ui-cashguard-cash';
import { UiCardPayModal } from '@app/components/model/ticket/ui-card/ui-card';
import { UiPaytefPayModal } from '@app/components/model/ticket/ui-paytef/ui-paytef';
import { UiToPrintertModal } from '@app/components/login/modal/print/ui-toprinter-modal';
import { deviceService } from '@app/modules/sockets';
import { tryCashlessCardPayment, chooseCardTerminal } from '@app/components/model/ticket/ui-cashguard-cash/ui-cashless-utils';
import { cashlessRefundStore } from '@app/components/model/ticket/ui-cashguard-cash/cashless-refund-store';

@Component({
    selector: 'ui-ticket-thumbnail',
    templateUrl:'./ui-thumb.html',
    styleUrls: [ 
        './ui-thumb.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTicketThumbnail extends languageSupport implements OnInit, AfterViewInit, OnDestroy {
    @Input('action') _action = 'view';  // 'view' / 'merge'
    @Input('place') _place: Place = null;
    @Input('ticket') _ticket: Ticket = null;

    /**
     * Slim-device cash lockout — see placeBlocksCash(). The thumbnail
     * exposes a quick "pay cash" action when the ticket is to-pay; we
     * gate that button on this getter so the per-terminal `nocash`
     * flag also wins for the in-line shortcut.
     */
    get BlockCash(){
        return placeBlocksCash(this._place, this.view);
    }

    private _elapsed_time = null;
    get ElapsedTime(){
        return this._elapsed_time;
    }

    set ElapsedTime(value){
        this._elapsed_time = value;
        this.change.markForCheck();
    }

    // Name of the POS (till) that owns this ticket — shown under the
    // payment/date in multi-POS so each ticket says where it was charged.
    // Empty string ('') in single-POS or when unresolved (Ticket.tillName),
    // so the template's *ngIf hides it.
    get TpvName(){
        return this._ticket ? this._ticket.tillName : '';
    }

    get PaidTime(){
        let _date = this._ticket.paidon;
        if (_date){
            let _dd = _date.getDate();
            let _mm = _date.getMonth()+1;
            let _yy = _date.getFullYear();
    
            let _HH = ("00" + _date.getHours()).slice(-2);
            let _MM = ("00" + _date.getMinutes()).slice(-2);  
            
            return _dd + "/" + _mm + "/" + _yy + " " + _HH + ":" + _MM;
        }
        
        // if paid to account then display the account name
        if (this._ticket.payment == 'PAYACCOUNT'){
            if (this._ticket.account){
                return this._ticket.account.name.toUpperCase();
            }
        }

        return null;
    }

    private _get_elapsed(){
        let _date = this._ticket.created;

        if (_date.getTime() < ((new Date()).getTime() - AppConstants.recentMs)){
            let _dd = _date.getDate();
            let _mm = _date.getMonth()+1;
            let _yy = _date.getFullYear();

            let _HH = ("00" + _date.getHours()).slice(-2);
            let _MM = ("00" + _date.getMinutes()).slice(-2);

            return _dd + "/" + _mm + "/" + _yy + " " + _HH + ":" + _MM;
        }
        else {
            let _min = Math.round(((new Date()).getTime() - _date.getTime()) / 60000);

            if (_min < 1){
                return this.lang.tr('@elapsed_time_just_now');            
            }
            
            if (_min < 60){
                return this.lang.tr('@elapsed_time_minutes', [ _min ]);
            }
            
            let _hh = Math.floor(_min / 60);
            let _mm = Math.floor(_min % 60);
    
            return this.lang.tr('@elapsed_time_hours', [ _hh, _mm]);    
        }
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private data: dataService, private devsvc: deviceService, public view: viewService, private cashlessStore: cashlessRefundStore) {
        super(lang, change);
    }

    private _recent_subscription = null;
    private _place_subscription = null;
    private _ticket_subscription = null;

    private _refreshTicket(){
        this.ElapsedTime = this._get_elapsed();
        this.change.markForCheck();
    }

    ngOnInit(){
        this._elapsed_time = this._get_elapsed();

        this._recent_subscription = this.data.Clock.OnOneMinuteTick.subscribe(
        data => {
            this._refreshTicket();
        });
    
        this._place_subscription= this._place.OnTargetRefresh('TICKET').subscribe(
        data => {
            this._refreshTicket();
        })

        this._ticket_subscription = this._ticket.OnRefresh.subscribe(
        data => {
            this.change.markForCheck();
        });
    }

    ngAfterViewInit() {
        this.change.markForCheck();
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._recent_subscription){
            this._recent_subscription.unsubscribe();
            this._recent_subscription = null;
        }

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this._ticket_subscription){
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    private _sliding = false;

    async OnSliding(){
        this._sliding = true;
        setTimeout(() => {
            this._sliding = false;
        }, 250)
    }

    async OnSlideClick(event, slider){
        if (this._sliding){
            event.stopPropagation();
        }
        else {
            slider.close();
        }
    }

    async PrepareTicket(event){
        event.stopPropagation();

        this._ticket.status = 'RD';
        if (this._ticket.DoCommit()){
            this._place.DoRefresh('TICKET', true);
        }
    }

    private _waitmodal = false;
    // _ticket param (not this._ticket): aligns the modal target with the
    // setter + DoCommit target — this._ticket can be swapped by sync.
    private async OnPaycash(_ticket){
        // Antes exigia IsRecent (<8h): un pedido viejo se marcaba pagado SIN
        // abrir el cajon/datafono. Ahora cualquier ticket sin pagar cobra por la
        // maquina; solo se salta si el local no tiene modal de efectivo.
        if (!this._place.CashModal){
            return true;
        }

        return new Promise<boolean>(async (resolve) => {
            if (!this._waitmodal){
                this._waitmodal = true;
                // Resuelve a que CashGuard mandar el cobro:
                //   1. mesa.till.smartdrawer (multi-POS correct path)
                //   2. _place.till.smartdrawer (TPV cobrando barra)
                //   3. cualquier CashGuard del place (comandero single-POS
                //      cobrando una mesa sin till asignado)
                let _cgInfo = this._place.smartdrawerForTicket(_ticket);
                let _smartdrawer = _cgInfo?.drawer || null;
                let _cgTill = _cgInfo?.till || null;
                let _tolocal = _cgInfo?.tolocal ?? false;

                let modal;
                if (_smartdrawer && _cgTill){
                    modal = await this.modalCtrl.create({
                        component: UiCashGuardCashPayModal,
                        cssClass: 'modal-cashguard',
                        backdropDismiss: false,
                        componentProps: {
                            tickets: [ _ticket ],
                            device: _smartdrawer,
                            place: _cgTill,
                            tolocal: _tolocal
                        }
                    });
                } else {
                    // Use regular cash modal
                    modal = await this.modalCtrl.create({
                        component: UiCashPayModal,
                        cssClass: 'modal-cash',
                        backdropDismiss: false,
                        componentProps: {
                            tickets: [ _ticket ]
                        }
                    });
                }

                modal.onDidDismiss().then(async (detail) => {
                    // Check if CashGuard modal requested fallback to cash payment
                    if (detail.data && detail.data['fallback']){
                        console.log('[SMARTDRAWER] Fallback to cash payment modal requested');
                        // Open regular cash modal as fallback
                        let cashModal = await this.modalCtrl.create({
                            component: UiCashPayModal,
                            cssClass: 'modal-cash',
                            backdropDismiss: false,
                            componentProps: {
                                tickets: [ _ticket ]
                            }
                        });

                        cashModal.onDidDismiss().then((cashDetail) => {
                            if (cashDetail.data){
                                _ticket.given = cashDetail.data['amount'];
                            }
                            resolve(cashDetail.data != null);
                        });

                        await cashModal.present();
                    } else {
                        if (detail.data){
                            _ticket.given = detail.data['amount'];
                        }
                        resolve(detail.data != null);
                    }
                });

                await modal.present();

                setTimeout(() => {
                    this._waitmodal = false;
                }, 100);
            }
        })
    }

    private async OnPaycard(_ticket){
        // Antes exigia IsRecent (<8h) y marcaba pagado el ticket viejo SIN pasar
        // por el datafono. Ahora cualquier ticket sin pagar cobra por la maquina
        // (cae a manual/CardModal si no hay terminal).

        // Selector de datáfono UNIFICADO (helper compartido): si hay datáfonos de
        // AMBOS tipos (PROSEGUR cashless + PAYTEF por IP), deja elegir al cajero.
        // Con un solo tipo devuelve 'default' y sigue el flujo de siempre.
        let _cgInfo = this._place.smartdrawerForTicket(_ticket);
        // Guard anti doble-tap DURANTE el selector (ver ui-view): dos toques
        // rápidos abrían dos pickers / dos cobros. Se libera al cerrarse el picker.
        if (this._waitmodal){ return false; }
        this._waitmodal = true;
        let _choice;
        try {
            _choice = await chooseCardTerminal(this.modalCtrl, this.lang, this._place, this.view, _cgInfo);
        } finally {
            this._waitmodal = false;
        }
        if (_choice.kind !== 'default'){
            if (_choice.kind === 'cancelled'){ return false; }
            if (_choice.kind === 'manual'){
                if (!this._place.CardModal){ return true; }
                return this._openManualCardModal([ _ticket ]);
            }
            if (_choice.kind === 'paytef'){
                return this._openPaytefModal([ _ticket ], _choice.terminal);
            }
            // prosegur: cobro cashless por el datáfono elegido.
            if (this._waitmodal){ return false; }
            this._waitmodal = true;
            let _r: boolean | null;
            try {
                _r = await tryCashlessCardPayment(this.devsvc, {
                    modalCtrl: this.modalCtrl,
                    cashlessStore: this.cashlessStore,
                    hasCardModal: !!this._place.CardModal,
                    chosenTerminal: _choice.terminal,
                    openManual: (tk) => this._openManualCardModal(tk),
                    onModalDismissed: () => { this._waitmodal = false; }
                }, [ _ticket ], _cgInfo);
            } finally {
                this._waitmodal = false;
            }
            return _r === null ? false : _r;
        }

        // Cajon con datafono (PROSEGUR/CASHLOGY): la tarjeta se cobra por el
        // datafono del cajon (igual que ui-view/ui-edit). Sin esto, un local con
        // SOLO datafono de cajon caia a resolvePaymentTerminals (paytef-only) ->
        // lista vacia -> se daba el PAYCARD por bueno SIN cobrar. El guard
        // _waitmodal se mantiene durante el intento cashless (que resuelve al
        // cerrarse la modal) y se libera antes de un posible fallback manual.
        if (this._waitmodal){
            return false;
        }
        this._waitmodal = true;
        let _cgCard: boolean | null;
        try {
            _cgCard = await tryCashlessCardPayment(this.devsvc, {
                modalCtrl: this.modalCtrl,
                cashlessStore: this.cashlessStore,   // indexar la referencia para el abono cashless posterior
                hasCardModal: !!this._place.CardModal,
                openManual: (tk) => this._openManualCardModal(tk),
                onModalDismissed: () => { this._waitmodal = false; }
            }, [ _ticket ], _cgInfo);
        } finally {
            this._waitmodal = false;
        }
        if (_cgCard !== null){
            return _cgCard;
        }

        // Paytef is independent from CardModal: if a terminal is configured
        // we always route the payment through its modal.
        let _terminals = resolvePaymentTerminals(this._place, this.view);

        if (_terminals.length > 0) {
            let _pick = await pickPaymentTerminal(this.modalCtrl, this.lang, _terminals, this.view);
            if (!_pick){
                return false;
            }
            if (isPaymentTerminalManual(_pick)){
                if (!this._place.CardModal){
                    return true;
                }
                return this._openManualCardModal([ _ticket ]);
            }
            return this._openPaytefModal([ _ticket ], _pick);
        }

        if (!this._place.CardModal){
            return true;
        }

        return new Promise<boolean>(async (resolve) => {
            if (this._waitmodal){
                resolve(false);   // modal en vuelo: no colgar al caller (doble-tap)
                return;
            }
            this._waitmodal = true;
            try {
                const modal = await this.modalCtrl.create({
                    component: UiCardPayModal,
                    cssClass: 'modal-card',
                    backdropDismiss: false,
                    componentProps: {
                        tickets: [ _ticket ]
                    }
                });

                modal.onDidDismiss().then((detail) => {
                    resolve(detail.data);
                });

                await modal.present();
            } catch(e){
                console.error('[PAY] card modal error', e);
                resolve(false);
            } finally {
                setTimeout(() => { this._waitmodal = false; }, 100);
            }
        })
    }

    private async _openManualCardModal(tickets): Promise<boolean> {
        return new Promise<boolean>(async (resolve) => {
            if (this._waitmodal){
                resolve(false);   // modal en vuelo: no colgar al caller
                return;
            }
            this._waitmodal = true;
            try {
                const modal = await this.modalCtrl.create({
                    component: UiCardPayModal,
                    cssClass: 'modal-card',
                    backdropDismiss: false,
                    componentProps: { tickets: tickets }
                });

                modal.onDidDismiss().then((detail) => {
                    resolve(!!detail.data);
                });

                await modal.present();
            } catch(_e){
                console.error('[PAY] card modal error', _e);
                resolve(false);
            } finally {
                setTimeout(() => { this._waitmodal = false; }, 100);
            }
        });
    }

    private async _openPaytefModal(tickets, terminal): Promise<boolean> {
        return new Promise<boolean>(async (resolve) => {
            if (this._waitmodal){
                resolve(false);   // modal en vuelo: no colgar al caller
                return;
            }
            this._waitmodal = true;
            try {
                const modal = await this.modalCtrl.create({
                    component: UiPaytefPayModal,
                    cssClass: 'modal-paytef',
                    backdropDismiss: false,
                    componentProps: {
                        tickets: tickets,
                        terminal: terminal,
                        place: this._place
                    }
                });

                modal.onDidDismiss().then((detail) => {
                    resolve(detail.data && (detail.data.approved || detail.data.manual) ? true : false);
                });

                await modal.present();
            } catch(_e){
                console.error('[PAY] paytef modal error', _e);
                resolve(false);
            } finally {
                setTimeout(() => { this._waitmodal = false; }, 100);
            }
        });
    }

    @Output('onInvoice') _onInvoice = new EventEmitter<any>();
    async PayTicket(event, payment){
        event.stopPropagation();

        // capture once: this._ticket can be swapped by sync mid-flow.
        const _ticket = this._ticket;

        let _dopay = true;
        if (payment == 'PAYCASH'){
            _dopay = await this.OnPaycash(_ticket);
        }

        if (payment == 'PAYCARD'){
            _dopay = await this.OnPaycard(_ticket);
        }

        if (_dopay){
            if (_ticket.ToInvoice){
                _ticket.pendpay = payment;
                this._onInvoice.next();
            }
            else {
                _ticket.payment = payment;
                _ticket.DoCommit().then(
                data => {
                    if (data) this._place.DoRefresh('TICKET', true);
                });
            }
        }
    }

    private _print_pressed_timeout = null;
    private _print_ignore_selected = false;

    private get _SelectPrinter(){
        return this._place.MultiplePOS && (!this._place.IsPOSDevice);
    }

    async PrintTicket(event, slider){
        event.stopPropagation();

        slider.close();
        if (!this._print_ignore_selected){
            if (this._SelectPrinter){
                this._PrintTicketModal();
            }
            else {
                this._ticket.DoPrint(false);    
            }
        }
    }    

    private _waittoprintmodal = false;
    private async _PrintTicketModal(slider = null){
        if (slider){
            slider.close();
        }

        if (!this._waittoprintmodal){            
            this._waittoprintmodal = true;

            const modal = await this.modalCtrl.create({
                component: UiToPrintertModal,
                cssClass: 'ticket-modal',
                componentProps: { 
                    ticket: this._ticket
                }                                    
            });

            await modal.present();
        }

        setTimeout(() => {
            this._waittoprintmodal = false;
        }, 100);
    }

    OnPrintPressed(down, slider){
        if (down){
            if (this._print_pressed_timeout){
                clearTimeout(this._print_pressed_timeout);
                this._print_pressed_timeout = null;
            }

            this._print_pressed_timeout = setTimeout(() => {
                this._PrintTicketModal(slider);

                this._print_ignore_selected = true;
                setTimeout(() => {  // only ignore on current loop
                    this._print_ignore_selected = false;
                }, 0);                
            }, 1000)
        }
        else {
            if (this._print_pressed_timeout){
                clearTimeout(this._print_pressed_timeout);
                this._print_pressed_timeout = null;
            }
        }
    }
}
